// Pure statistical helpers. No I/O, no React — easy to test, easy to extend.
//
// Conventions:
//   - "WPM" follows the standard typing-test definition: 5 characters = 1 word.
//   - "Accuracy" is correct keystrokes / total keystrokes, where total
//     EXCLUDES Backspace (Backspace is tracked separately on the result).

import type {
  ActivityKind,
  CheckpointRecord,
  CurriculumTierId,
  DailySummary,
  LessonResult,
  PracticeMode,
  RoundPresentation,
  RoundResult,
  TrinRecord,
  UserProfile,
} from '../types';
/** Words per minute. */
export function wpm(correctChars: number, elapsedMs: number): number {
  if (elapsedMs <= 0) return 0;
  const minutes = elapsedMs / 60_000;
  if (minutes === 0) return 0;
  return (correctChars / 5) / minutes;
}

/** Accuracy as a fraction in [0, 1]. Returns 1 when nothing has been typed
 *  yet so the UI doesn't show a confusing 0% before the first keystroke. */
export function accuracy(
  correctKeystrokes: number,
  totalKeystrokes: number,
): number {
  if (totalKeystrokes <= 0) return 1;
  return correctKeystrokes / totalKeystrokes;
}

/** Top N keys with the highest error count, descending. */
export function topWeakKeys(
  errorsByKey: Record<string, number>,
  n: number,
): Array<{ key: string; count: number }> {
  return Object.entries(errorsByKey)
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, n);
}

/** Top N keys with the highest average response time, descending. Only
 *  keys with at least `minSamples` samples are considered so a single
 *  freak measurement can't make a key look slow. */
export function topSlowKeys(
  responseTimes: Record<string, number[]> | Record<string, number>,
  n: number,
  minSamples = 3,
): Array<{ key: string; avgMs: number; samples: number }> {
  const rows: Array<{ key: string; avgMs: number; samples: number }> = [];
  for (const [key, val] of Object.entries(responseTimes)) {
    if (Array.isArray(val)) {
      if (val.length < minSamples) continue;
      const avg = val.reduce((a, b) => a + b, 0) / val.length;
      rows.push({ key, avgMs: avg, samples: val.length });
    } else if (typeof val === 'number') {
      rows.push({ key, avgMs: val, samples: minSamples });
    }
  }
  rows.sort((a, b) => b.avgMs - a.avgMs);
  return rows.slice(0, n);
}

/**
 * XP awarded for a single lesson.
 *
 * Formula:
 *   base 50
 *   + accuracy bonus  : round((accuracy - 0.80) * 200)   // up to +40 at 100%
 *   + speed bonus     : round(min(wpm, 60) / 2)          // up to +30 at 60 wpm
 *   + completion bonus: +20 if passedAllRounds
 *   clamped to [0, 170]
 */
export function xpForLesson(result: LessonResult): number {
  const base = 50;
  const accBonus = Math.round((result.accuracy - 0.8) * 200);
  const speedBonus = Math.round(Math.min(result.wpm, 60) / 2);
  const completionBonus = result.passedAllRounds ? 20 : 0;
  return Math.max(0, Math.min(170, base + accBonus + speedBonus + completionBonus));
}

/** Average accuracy across an array of results. */
export function averageAccuracy(results: Array<{ accuracy: number }>): number {
  if (results.length === 0) return 0;
  const sum = results.reduce((acc, r) => acc + r.accuracy, 0);
  return sum / results.length;
}

/** Average WPM across an array of results. */
export function averageWpm(results: Array<{ wpm: number }>): number {
  if (results.length === 0) return 0;
  const sum = results.reduce((acc, r) => acc + r.wpm, 0);
  return sum / results.length;
}

// ---------------------------------------------------------------------------
// Lesson-level helpers (aggregate rounds into a LessonResult)
// ---------------------------------------------------------------------------

/** Sum a list of round results into a LessonResult. */
export function aggregateRounds(
  trinId: string,
  tierId: CurriculumTierId,
  startedAt: string,
  rounds: RoundResult[],
): LessonResult {
  const durationMs = rounds.reduce((a, r) => a + r.durationMs, 0);
  const correctChars = rounds.reduce((a, r) => a + r.correctChars, 0);
  const totalKeystrokes = rounds.reduce(
    (a, r) => a + r.totalKeystrokes,
    0,
  );
  const mistakes = rounds.reduce((a, r) => a + r.mistakes, 0);
  const backspaces = rounds.reduce((a, r) => a + r.backspaces, 0);

  const errorsByKey: Record<string, number> = {};
  const responseTimes: Record<string, number[]> = {};
  for (const r of rounds) {
    for (const [k, v] of Object.entries(r.errorsByKey)) {
      errorsByKey[k] = (errorsByKey[k] ?? 0) + v;
    }
    for (const [k, v] of Object.entries(r.responseTimes)) {
      if (!responseTimes[k]) responseTimes[k] = [];
      responseTimes[k].push(...v);
    }
  }

  return {
    trinId,
    tierId,
    startedAt,
    durationMs,
    rounds,
    wpm: wpm(correctChars, durationMs),
    accuracy: accuracy(correctChars, totalKeystrokes),
    correctChars,
    totalKeystrokes,
    mistakes,
    backspaces,
    errorsByKey,
    responseTimes,
    passedAllRounds: rounds.every((r) => r.completed),
  };
}

/** Find the round with the best combined score in a lesson. We use a
 *  simple weighted blend of accuracy and WPM so neither alone wins. */
export function bestRound(rounds: RoundResult[]): RoundResult | null {
  if (rounds.length === 0) return null;
  let best = rounds[0];
  let bestScore = scoreRound(best);
  for (let i = 1; i < rounds.length; i++) {
    const s = scoreRound(rounds[i]);
    if (s > bestScore) {
      best = rounds[i];
      bestScore = s;
    }
  }
  return best;
}

function scoreRound(r: RoundResult): number {
  return r.accuracy * 100 + Math.min(r.wpm, 60) / 2;
}

// ---------------------------------------------------------------------------
// Readiness + recommendation
// ---------------------------------------------------------------------------

/** Did this lesson clear the current trin? */
export function isTrinCleared(result: LessonResult): boolean {
  return result.accuracy >= 0.95 && result.passedAllRounds;
}

/** @deprecated Use isTrinCleared */
export const isLevelCleared = isTrinCleared;

export type Recommendation =
  | { kind: 'advance'; reason: string }
  | { kind: 'repeat'; reason: string }
  | { kind: 'continue'; reason: string };

/**
 * Rule-based recommendation for what the user should do next.
 *
 * The tone is encouraging but precise — these strings end up rendered
 * directly in the LessonSummary banner.
 */
export function recommend(result: LessonResult): Recommendation {
  if (isTrinCleared(result)) {
    return {
      kind: 'advance',
      reason: 'Du har styr på dette trin. Klar til næste trin.',
    };
  }

  if (!result.passedAllRounds) {
    return {
      kind: 'repeat',
      reason:
        'Du afsluttede ikke alle runder. Gentag trinnet og gennemfør hver runde.',
    };
  }

  if (result.accuracy < 0.9) {
    return {
      kind: 'repeat',
      reason:
        'Du kommer godt igennem, men nøjagtigheden skal stabiliseres før næste trin.',
    };
  }

  return {
    kind: 'continue',
    reason:
      'Du er tæt på. En lektion mere på dette trin og du er klar til at gå videre.',
  };
}

/** A short, specific feedback string after a single round, intended for
 *  the intermission card. */
export function roundFeedback(r: RoundResult): string {
  const weakest = topWeakKeys(r.errorsByKey, 1)[0];
  if (!r.completed && r.targetLength > 0) {
    return 'Du afsluttede runden tidligt — prøv at skrive alle tegn næste gang.';
  }
  if (r.accuracy >= 0.98) {
    return 'Næsten fejlfrit. Flot rytme.';
  }
  if (r.accuracy >= 0.95) {
    return 'Stærk runde. Du rammer det rigtige meget oftere end ikke.';
  }
  if (weakest) {
    const k = weakest.key === ' ' ? 'mellemrum' : weakest.key.toUpperCase();
    return `Du tøvede mest på ${k}. Vi tager den med igen næste runde.`;
  }
  return 'Godt forsøg. Næste runde bygger videre.';
}

/** Lesson-level encouraging summary message. */
export function lessonSummaryMessage(result: LessonResult): string {
  if (isTrinCleared(result)) {
    return 'Din nøjagtighed er stærk. Næste gang reducerer vi ledetråde tidligere.';
  }
  const slowest = topSlowKeys(result.responseTimes, 1, 3)[0];
  const weakest = topWeakKeys(result.errorsByKey, 1)[0];
  if (weakest && weakest.count >= 3) {
    const k = weakest.key === ' ' ? 'mellemrum' : weakest.key.toUpperCase();
    return `Du tøver stadig på ${k}. Vi tilføjer mere ${k}-genkald i næste lektion.`;
  }
  if (slowest && slowest.avgMs > 800) {
    const k = slowest.key === ' ' ? 'mellemrum' : slowest.key.toUpperCase();
    return `Du er hurtig nok, men ${k} kræver lidt mere tænketid. Vi øver den igen.`;
  }
  return 'Du er hurtig nok, men nøjagtigheden skal stabiliseres før næste niveau.';
}

// ---------------------------------------------------------------------------
// Adaptive helpers used by lessonGenerator.ts
// ---------------------------------------------------------------------------

/** Slow keys, as a Record<key, avgMs>, derived from a user's running EMA.
 *  Only keys slower than `thresholdMs` are returned — these are the keys
 *  worth biasing recall drills toward. */
export function slowKeysMap(
  responseTimeAvgs: Record<string, number>,
  thresholdMs = 700,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(responseTimeAvgs)) {
    if (v >= thresholdMs) out[k] = v;
  }
  return out;
}

/** Convenience: get a friendly name for a practice mode. */
export function modeLabel(mode: PracticeMode): string {
  switch (mode) {
    case 'guided':
      return 'Med ledetråde';
    case 'recall':
      return 'Genkald';
    case 'challenge':
      return 'Udfordring';
  }
}

// ---------------------------------------------------------------------------
// Per-trin records (dashboard + highscores)
// ---------------------------------------------------------------------------

export function getTrinRecord(
  user: UserProfile,
  trinId: string,
): TrinRecord | undefined {
  return user.trinRecords[trinId];
}

export function formatTrinRecordStats(
  record: TrinRecord | undefined,
  _trinId: string,
): { primary: string; secondary: string } | null {
  if (!record || record.attempts === 0) return null;
  const errPct = Math.round((1 - record.bestAccuracy) * 100);
  return {
    primary: `${errPct}% fejl`,
    secondary: `${record.bestWpm.toFixed(0)} wpm`,
  };
}

export function formatCheckpointRecordStats(
  record: CheckpointRecord | undefined,
): { primary: string; secondary: string } | null {
  if (!record || record.attempts === 0) return null;
  return {
    primary: `${record.bestWords} ord`,
    secondary: 'bedst',
  };
}

export function getCheckpointRecord(
  user: UserProfile,
  checkpointId: string,
): CheckpointRecord | undefined {
  return user.checkpointRecords?.[checkpointId];
}

export function mergeLessonIntoTrinRecord(
  prev: TrinRecord | undefined,
  result: LessonResult,
): TrinRecord {
  const wpmVal = result.wpm;
  const acc = result.accuracy;
  return {
    bestWpm: Math.max(prev?.bestWpm ?? 0, wpmVal),
    bestAccuracy: Math.max(prev?.bestAccuracy ?? 0, acc),
    attempts: (prev?.attempts ?? 0) + 1,
    lastPlayedAt: result.startedAt,
  };
}

export function mergeCheckpointRecord(
  prev: CheckpointRecord | undefined,
  wordsCompleted: number,
  startedAt: string,
): CheckpointRecord {
  return {
    bestWords: Math.max(prev?.bestWords ?? 0, wordsCompleted),
    attempts: (prev?.attempts ?? 0) + 1,
    lastPlayedAt: startedAt,
  };
}

// ---------------------------------------------------------------------------
// Cross-presentation aggregation (Fase 5)
// ---------------------------------------------------------------------------

export type PresentationKind = RoundPresentation['kind'];

const ALL_PRESENTATIONS: PresentationKind[] = [
  'standard',
  'dictation',
  'echo',
  'metronome',
  'shadow',
];

export interface PresentationStats {
  wpm: number;
  accuracy: number;
  n: number;
}

/**
 * Aggregate recent round results by presentation kind. Each entry is the
 * average WPM / accuracy across all rounds with that presentation, plus
 * how many rounds contributed. Useful for showing the user where they
 * are strong and where they need more drills.
 */
export function performanceByPresentation(
  results: LessonResult[],
): Record<PresentationKind, PresentationStats> {
  const out = {} as Record<PresentationKind, PresentationStats>;
  const accum: Record<PresentationKind, { wpm: number; acc: number; n: number }> =
    {} as Record<PresentationKind, { wpm: number; acc: number; n: number }>;
  for (const k of ALL_PRESENTATIONS) accum[k] = { wpm: 0, acc: 0, n: 0 };

  for (const r of results) {
    for (const round of r.rounds) {
      const kind = (round.presentationKind ?? 'standard') as PresentationKind;
      const bucket = accum[kind];
      if (!bucket) continue;
      bucket.wpm += round.wpm;
      bucket.acc += round.accuracy;
      bucket.n += 1;
    }
  }

  for (const k of ALL_PRESENTATIONS) {
    const b = accum[k];
    out[k] = {
      wpm: b.n === 0 ? 0 : b.wpm / b.n,
      accuracy: b.n === 0 ? 0 : b.acc / b.n,
      n: b.n,
    };
  }
  return out;
}

/**
 * Retention proxy: dictation accuracy vs visual ("standard") accuracy.
 * Positive value = user has internalized typing (similar performance
 * with or without seeing the text). Negative value = user is still
 * relying on visual scanning.
 *
 * Returns null if either bucket has fewer than `minSamples` rounds.
 */
export function dictationRetentionGap(
  results: LessonResult[],
  minSamples = 2,
): { gap: number; standardAcc: number; dictationAcc: number } | null {
  const stats = performanceByPresentation(results);
  const std = stats.standard;
  const dict = stats.dictation;
  if (std.n < minSamples || dict.n < minSamples) return null;
  return {
    gap: dict.accuracy - std.accuracy,
    standardAcc: std.accuracy,
    dictationAcc: dict.accuracy,
  };
}

// ---------------------------------------------------------------------------
// Daily progress rollups (langsigtet udvikling)
// ---------------------------------------------------------------------------

export const DAILY_SUMMARIES_CAP = 90;

/** Lokal kalenderdag som YYYY-MM-DD. */
export function localDateKey(d: Date = new Date()): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Lokal kalenderdag fra et ISO-tidsstempel. */
export function localDateFromIso(iso: string): string {
  return localDateKey(new Date(iso));
}

/** Syntetiske trin (varm-op, diktat, osv.) — tæller ikke i pensum-WPM. */
export function isExtraTrin(trinId: string): boolean {
  return trinId.startsWith('__');
}

export function mergeLessonIntoDailySummary(
  prev: DailySummary | undefined,
  result: LessonResult,
  date: string,
  xpGained: number,
): DailySummary {
  const isCurriculum = !isExtraTrin(result.trinId);
  const lessonCount = (prev?.lessonCount ?? 0) + 1;
  const curriculumLessonCount =
    (prev?.curriculumLessonCount ?? 0) + (isCurriculum ? 1 : 0);

  let avgWpm = prev?.avgWpm ?? 0;
  let avgAccuracy = prev?.avgAccuracy ?? 0;
  if (isCurriculum) {
    const prevCurN = prev?.curriculumLessonCount ?? 0;
    avgWpm =
      prevCurN === 0
        ? result.wpm
        : (prev!.avgWpm * prevCurN + result.wpm) / curriculumLessonCount;
    avgAccuracy =
      prevCurN === 0
        ? result.accuracy
        : (prev!.avgAccuracy * prevCurN + result.accuracy) /
          curriculumLessonCount;
  } else if (prev) {
    avgWpm = prev.avgWpm;
    avgAccuracy = prev.avgAccuracy;
  }

  const kind: ActivityKind = isExtraTrin(result.trinId)
    ? 'extra-lesson'
    : 'curriculum-lesson';
  const activityByKind = { ...(prev?.activityByKind ?? {}) };
  activityByKind[kind] = (activityByKind[kind] ?? 0) + 1;

  return {
    date,
    lessonCount,
    curriculumLessonCount,
    avgWpm,
    avgAccuracy,
    totalDurationMs: (prev?.totalDurationMs ?? 0) + result.durationMs,
    bestWpm: Math.max(
      prev?.bestWpm ?? 0,
      isCurriculum ? result.wpm : 0,
    ),
    xpGained: (prev?.xpGained ?? 0) + xpGained,
    totalKeystrokes: (prev?.totalKeystrokes ?? 0) + result.totalKeystrokes,
    correctChars: (prev?.correctChars ?? 0) + result.correctChars,
    sessionCount: (prev?.sessionCount ?? 0) + 1,
    activityByKind,
  };
}

/** Byg daglig rollup fra recentResults (migration / backfill). */
export function backfillDailySummariesFromRecentResults(
  results: LessonResult[],
): Record<string, DailySummary> {
  const out: Record<string, DailySummary> = {};
  for (const r of [...results].reverse()) {
    const date = localDateFromIso(r.startedAt);
    out[date] = mergeLessonIntoDailySummary(
      out[date],
      r,
      date,
      xpForLesson(r),
    );
  }
  return out;
}

export function pruneDailySummaries(
  summaries: Record<string, DailySummary>,
  cap = DAILY_SUMMARIES_CAP,
): Record<string, DailySummary> {
  const dates = Object.keys(summaries).sort();
  if (dates.length <= cap) return summaries;
  const keep = new Set(dates.slice(-cap));
  return Object.fromEntries(
    Object.entries(summaries).filter(([d]) => keep.has(d)),
  );
}

export type ProgressMetric =
  | 'keystrokes'
  | 'minutes'
  | 'sessions'
  | 'wpm'
  | 'accuracy';

export interface DailyProgressPoint {
  date: string;
  value: number | null;
  lessonCount: number;
  curriculumLessonCount: number;
  sessionCount: number;
  totalDurationMs: number;
  totalKeystrokes: number;
}

/** Sidste N kalenderdage med værdi eller null (ingen trin-lektion). */
export function dailyProgressSeries(
  summaries: Record<string, DailySummary> | undefined,
  days: number,
  metric: ProgressMetric,
): DailyProgressPoint[] {
  const out: DailyProgressPoint[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - i);
    const date = localDateKey(d);
    const s = summaries?.[date];
    const sessions = s?.sessionCount ?? s?.lessonCount ?? 0;
    let value: number | null = null;
    if (s) {
      if (metric === 'keystrokes') {
        value = sessions > 0 ? (s.totalKeystrokes ?? 0) : null;
      } else if (metric === 'minutes') {
        value =
          sessions > 0
            ? Math.round((s.totalDurationMs ?? 0) / 60_000)
            : null;
      } else if (metric === 'sessions') {
        value = sessions > 0 ? sessions : null;
      } else if (metric === 'wpm' && s.curriculumLessonCount > 0) {
        value = s.avgWpm;
      } else if (metric === 'accuracy' && s.curriculumLessonCount > 0) {
        value = s.avgAccuracy * 100;
      }
    }
    out.push({
      date,
      value,
      lessonCount: s?.lessonCount ?? 0,
      curriculumLessonCount: s?.curriculumLessonCount ?? 0,
      sessionCount: sessions,
      totalDurationMs: s?.totalDurationMs ?? 0,
      totalKeystrokes: s?.totalKeystrokes ?? 0,
    });
  }
  return out;
}

export interface ProgressTrend {
  delta: number | null;
  first: number | null;
  last: number | null;
  activeDays: number;
  curriculumDays: number;
  totalMinutes: number;
  totalLessons: number;
  totalKeystrokes: number;
  totalSessions: number;
}

export function progressTrend(
  points: DailyProgressPoint[],
): ProgressTrend {
  const withValue = points.filter((p) => p.value !== null);
  const activeDays = points.filter((p) => p.sessionCount > 0).length;
  const curriculumDays = points.filter((p) => p.curriculumLessonCount > 0).length;
  const totalMinutes = Math.round(
    points.reduce((a, p) => a + p.totalDurationMs, 0) / 60_000,
  );
  const totalLessons = points.reduce((a, p) => a + p.lessonCount, 0);
  const totalKeystrokes = points.reduce((a, p) => a + p.totalKeystrokes, 0);
  const totalSessions = points.reduce((a, p) => a + p.sessionCount, 0);

  if (withValue.length < 2) {
    return {
      delta: null,
      first: withValue[0]?.value ?? null,
      last: withValue[0]?.value ?? null,
      activeDays,
      curriculumDays,
      totalMinutes,
      totalLessons,
      totalKeystrokes,
      totalSessions,
    };
  }

  const first = withValue[0].value!;
  const last = withValue[withValue.length - 1].value!;
  return {
    delta: last - first,
    first,
    last,
    activeDays,
    curriculumDays,
    totalMinutes,
    totalLessons,
    totalKeystrokes,
    totalSessions,
  };
}

// ---------------------------------------------------------------------------
// WPM check-ins (solid repertoire tests)
// ---------------------------------------------------------------------------

export interface CheckInProgressPoint {
  date: string;
  wpm: number;
  accuracy: number;
  solidKeyCount: number;
}

export interface CheckInProgressSeries {
  rangeStart: string;
  rangeEnd: string;
  points: CheckInProgressPoint[];
}

function parseDateKey(key: string): Date {
  return new Date(`${key}T12:00:00`);
}

function addDaysToDateKey(key: string, deltaDays: number): string {
  const d = parseDateKey(key);
  d.setDate(d.getDate() + deltaDays);
  return localDateKey(d);
}

/** Kalenderdage mellem to YYYY-MM-DD-nøgler (inklusive endepunkter = 0). */
export function daysBetweenDateKeys(start: string, end: string): number {
  const ms = parseDateKey(end).getTime() - parseDateKey(start).getTime();
  return Math.round(ms / 86_400_000);
}

function isBetterCheckIn(
  a: { wpm: number; accuracy: number },
  b: { wpm: number; accuracy: number },
): boolean {
  if (a.wpm !== b.wpm) return a.wpm > b.wpm;
  return a.accuracy > b.accuracy;
}

/** Bedste WPM pr. kalenderdag (max `maxDays` tilbage, akse fra første test til i dag). */
export function checkInProgressSeries(
  checkIns:
    | Array<{
        takenAt: string;
        wpm: number;
        accuracy: number;
        solidKeyCount: number;
      }>
    | undefined,
  maxDays = 180,
): CheckInProgressSeries {
  const rangeEnd = localDateKey();
  const windowStart = addDaysToDateKey(rangeEnd, -(maxDays - 1));

  if (!checkIns?.length) {
    return { rangeStart: windowStart, rangeEnd, points: [] };
  }

  const byDay = new Map<
    string,
    { wpm: number; accuracy: number; solidKeyCount: number }
  >();

  for (const c of checkIns) {
    const date = localDateFromIso(c.takenAt);
    const existing = byDay.get(date);
    const candidate = {
      wpm: c.wpm,
      accuracy: c.accuracy,
      solidKeyCount: c.solidKeyCount,
    };
    if (!existing || isBetterCheckIn(candidate, existing)) {
      byDay.set(date, candidate);
    }
  }

  const points = [...byDay.entries()]
    .filter(([date]) => date >= windowStart && date <= rangeEnd)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, v]) => ({
      date,
      wpm: v.wpm,
      accuracy: v.accuracy * 100,
      solidKeyCount: v.solidKeyCount,
    }));

  const rangeStart =
    points.length > 0 ? points[0]!.date : windowStart;

  return { rangeStart, rangeEnd, points };
}

export function checkInProgressTrend(
  points: CheckInProgressPoint[],
): { delta: number | null; first: number | null; last: number | null } {
  if (points.length < 2) {
    return {
      delta: null,
      first: points[0]?.wpm ?? null,
      last: points[0]?.wpm ?? null,
    };
  }
  const first = points[0]!.wpm;
  const last = points[points.length - 1]!.wpm;
  return { delta: last - first, first, last };
}
